
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Meta from "@/components/Meta";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { HomeIcon, ShoppingCart, HeadphonesIcon, BugIcon, Search, ArrowLeft } from "lucide-react";
import { FloatingSocialWidget } from "@/components/SocialShare";
import { toast } from "sonner";


const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [recentlyViewed, setRecentlyViewed] = useState<string[]>([]);

  useEffect(() => {
    // Log 404s for tracking purposes
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );

    // Track 404 error with analytics
    try {
      // Get recently viewed pages from localStorage
      const recentViewsStr = localStorage.getItem("recentlyViewed");
      if (recentViewsStr) {
        const recentViews = JSON.parse(recentViewsStr);
        setRecentlyViewed(recentViews.slice(0, 3)); // Show only 3 most recent
      }
    } catch (err) {
      console.error("Error retrieving recently viewed pages:", err);
    }
  }, [location.pathname]);

  // Common misspellings and old URLs redirects
  const redirectMap: Record<string, string> = {
    "/customise": "/customize",
    "/gaming": "/gaming-pcs",
    "/prebuilds": "/gaming-pcs",
    "/custom": "/customize",
    "/workstation": "/workstations",
    "/about-us": "/about",
  };

  useEffect(() => {
    const path = location.pathname.toLowerCase();
    const redirectTo = redirectMap[path];
    
    if (redirectTo) {
      window.location.href = redirectTo;
    }
  }, [location.pathname]);

  // Handle search submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      toast.info(`Searching for "${searchQuery}"...`);
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Suggest correction based on URL pattern
  const getSuggestion = () => {
    const path = location.pathname.toLowerCase();
    
    // Check for common patterns
    if (path.includes("product")) return "/gaming-pcs";
    if (path.includes("blog") || path.includes("news")) return "/articles";
    if (path.includes("contact")) return "/support";
    return null;
  };

  const suggestedPath = getSuggestion();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gaming-darker">
      <Meta 
        title="Page Not Found | BattleforgePC"
        description="The page you're looking for cannot be found. Explore our custom gaming PCs and prebuilt systems instead."
      />
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <FloatingSocialWidget />

      <div className="container py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="mb-8 text-gaming-blue hover:text-gaming-blue/80"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go Back
          </Button>

          <div className="text-center mb-12">
            <div className="mb-8 relative">
              <div className="absolute inset-0 blur-xl bg-gaming-red/20 rounded-full"></div>
              <svg className="w-32 h-32 mx-auto text-gaming-red relative z-10" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M64 8L8 64L64 120L120 64L64 8Z" stroke="currentColor" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M64 40V72" stroke="currentColor" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="64" cy="88" r="4" fill="currentColor" />
              </svg>
              <div className="animate-ping absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-gaming-red/20"></div>
            </div>
            
            <h1 className="text-6xl font-bold text-gaming-red mb-4 drop-shadow-glow">404</h1>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-6">Game Over - Page Not Found</h2>
            <div className="h-1 w-32 bg-gradient-to-r from-gaming-blue via-gaming-purple to-gaming-red mx-auto mb-6"></div>
            
            <p className="text-gray-300 mb-8 max-w-xl mx-auto text-lg">
              The page you were looking for has vanished into the digital void.
              {suggestedPath && (
                <span> Are you looking for our <Link to={suggestedPath} className="text-gaming-blue hover:underline">
                  {suggestedPath.replace("/", "").replace("-", " ")} page
                </Link>?</span>
              )}
            </p>

            <form onSubmit={handleSearch} className="max-w-md mx-auto mb-12">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search our site..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-gaming-dark/80 border border-gaming-light-gray/30 rounded-full py-3 px-5 pr-12 text-white focus:outline-none focus:ring-2 focus:ring-gaming-blue"
                />
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gaming-blue hover:text-gaming-purple">
                  <Search className="h-5 w-5" />
                  <span className="sr-only">Search</span>
                </button>
              </div>
            </form>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
            <Button asChild variant="default" className="w-full bg-gaming-blue hover:bg-gaming-blue/80">
              <Link to="/" className="flex items-center justify-center gap-2 py-6">
                <HomeIcon className="w-5 h-5" />
                Return to Homepage
              </Link>
            </Button>
            
            <Button asChild variant="secondary" className="w-full bg-gaming-purple hover:bg-gaming-purple/80">
                            <Link to="/gaming-pcs" className="flex items-center justify-center gap-2 py-6">
                <ShoppingCart className="w-5 h-5" />
                Browse Gaming PCs
              </Link>
            </Button>
            
            <Button asChild variant="outline" className="w-full border-gaming-red hover:bg-gaming-red/10">
              <Link to="/support" className="flex items-center justify-center gap-2 py-6">
                <HeadphonesIcon className="w-5 h-5" />
                Contact Support
              </Link>
            </Button>
          </div>
          
          {recentlyViewed.length > 0 && (
            <div className="mb-12">
              <h3 className="text-xl font-semibold text-white mb-4">Recently Viewed</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {recentlyViewed.map((path, index) => (
                  <Card key={index} className="bg-gaming-dark/50 border border-gaming-light-gray/30 hover:border-gaming-blue/50 transition-all">
                    <CardContent className="p-4">
                      <Link 
                        to={path} 
                        className="text-gaming-blue hover:underline block"
                      >
                        {path.replace("/", "").replace(/-/g, " ")}
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
          
          <div className="px-6 py-4 bg-gaming-dark/50 rounded-lg border border-gaming-light-gray/30 inline-flex items-center gap-2 mx-auto mb-12 w-full justify-center">
            <BugIcon className="w-5 h-5 text-gaming-blue" />
            <span className="text-sm text-gray-300">Did you find a broken link? <Link to="/support" className="text-gaming-blue hover:underline">Report it here</Link></span>
          </div>
          
          <div className="mt-12 border-t border-gaming-light-gray/20 pt-8">
            <h3 className="text-xl font-semibold text-white mb-4 text-center">Popular Pages</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-gaming-dark/30 border border-gaming-light-gray/20 hover:border-gaming-blue/30 transition-all">
                <CardContent className="p-3">
                  <h4 className="font-medium text-sm text-gaming-blue mb-2">Gaming PCs</h4>
                  <div className="flex flex-col space-y-1 text-sm">
                    <Link to="/customize" className="text-gray-300 hover:text-white">Customize Your PC</Link>
                                        <Link to="/gaming-pcs" className="text-gray-300 hover:text-white">Pre-Built Gaming PCs</Link>
                    <Link to="/gaming-pcs" className="text-gray-300 hover:text-white">Gaming Computers</Link>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gaming-dark/30 border border-gaming-light-gray/20 hover:border-gaming-blue/30 transition-all">
                <CardContent className="p-3">
                  <h4 className="font-medium text-sm text-gaming-blue mb-2">Hardware</h4>
                  <div className="flex flex-col space-y-1 text-sm">
                    <Link to="/components" className="text-gray-300 hover:text-white">PC Components</Link>
                    <Link to="/workstations" className="text-gray-300 hover:text-white">Workstation PCs</Link>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gaming-dark/30 border border-gaming-light-gray/20 hover:border-gaming-blue/30 transition-all">
                <CardContent className="p-3">
                  <h4 className="font-medium text-sm text-gaming-blue mb-2">Support</h4>
                  <div className="flex flex-col space-y-1 text-sm">
                    <Link to="/about" className="text-gray-300 hover:text-white">About Us</Link>
                    <Link to="/support" className="text-gray-300 hover:text-white">Customer Support</Link>
                    <Link to="/order-tracking" className="text-gray-300 hover:text-white">Order Tracking</Link>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gaming-dark/30 border border-gaming-light-gray/20 hover:border-gaming-blue/30 transition-all">
                <CardContent className="p-3">
                  <h4 className="font-medium text-sm text-gaming-blue mb-2">Resources</h4>
                  <div className="flex flex-col space-y-1 text-sm">
                    <Link to="/articles" className="text-gray-300 hover:text-white">Articles</Link>
                    <Link to="/sitemap" className="text-gray-300 hover:text-white">Sitemap</Link>
                    <Link to="/terms" className="text-gray-300 hover:text-white">Terms & Conditions</Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
